/**
 * Share Utilities
 * Helper functions for social sharing functionality
 */

/**
 * Get the base URL for the application
 * Uses environment variable in production, localhost in development
 */
export const getBaseUrl = (): string => {
  // Check if we have a VITE_APP_URL environment variable
  if (import.meta.env.VITE_APP_URL) {
    return import.meta.env.VITE_APP_URL;
  }
  
  // In production, use the production domain
  if (import.meta.env.PROD) {
    return 'https://spark.playmoments.app';
  }
  
  // In development, use localhost
  return 'http://localhost:5173';
};

/**
 * Generate activity detail page URL
 */
export const getActivityShareUrl = (activityId: number | string): string => {
  const baseUrl = getBaseUrl();
  return `${baseUrl}/activity/${activityId}`;
};

/**
 * Generate activity image proxy URL
 * This URL redirects to fresh S3 signed URLs
 */
export const getActivityImageProxyUrl = (activityId: number | string): string => {
  // Use production API URL for images in production, local API in dev
  const apiBaseUrl = import.meta.env.PROD 
    ? 'https://spark.playmoments.app/api'
    : 'http://localhost:3000/api';
  
  return `${apiBaseUrl}/activities/images/${activityId}/cover`;
};

/**
 * Check if Web Share API is supported
 */
export const isWebShareSupported = (): boolean => {
  return typeof navigator !== 'undefined' && 'share' in navigator;
};

/**
 * Share via Web Share API (native mobile share)
 * Returns true if share was initiated, false if cancelled or failed
 */
export interface ShareData {
  title: string;
  text?: string;
  url: string;
}

export const shareViaWebShareAPI = async (data: ShareData): Promise<boolean> => {
  if (!isWebShareSupported()) {
    throw new Error('Web Share API is not supported in this browser');
  }

  try {
    await navigator.share({
      title: data.title,
      text: data.text,
      url: data.url,
    });
    return true; // Share was successful
  } catch (error) {
    // User cancelled the share dialog
    if (error instanceof Error && error.name === 'AbortError') {
      console.log('[Share] User cancelled share');
      return false;
    }
    // Other errors
    console.error('[Share] Error sharing:', error);
    throw error;
  }
};

/**
 * Generate WhatsApp share URL
 * Opens WhatsApp with pre-filled message
 */
export const generateWhatsAppShareUrl = (activityUrl: string, title?: string): string => {
  const text = title 
    ? `Check out this activity: ${title}\n${activityUrl}`
    : activityUrl;
  
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
};

/**
 * Generate Facebook share URL
 * Opens Facebook sharer dialog
 */
export const generateFacebookShareUrl = (activityUrl: string): string => {
  return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(activityUrl)}`;
};

/**
 * Copy text to clipboard
 * Returns true if successful, false otherwise
 */
export const copyToClipboard = async (text: string): Promise<boolean> => {
  // Modern Clipboard API (preferred)
  if (navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('[Share] Clipboard API error:', error);
      // Fall through to fallback method
    }
  }

  // Fallback for older browsers
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (successful) {
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('[Share] Clipboard fallback error:', error);
    return false;
  }
};

